import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Event as EventIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { DatabasePreviewSession } from '../../../types/onboarding.types';
import dayjs from 'dayjs';

interface DatabasePreviewProps {
  sessions: DatabasePreviewSession[];
  templateName: string;
  userName: string;
  userEmail: string;
}

const DatabasePreview: React.FC<DatabasePreviewProps> = ({
  sessions,
  templateName,
  userName,
  userEmail,
}) => {
  const formatTime = (dateTimeString: string): string => {
    return dayjs(dateTimeString).format('h:mm A');
  };

  const formatDate = (dateTimeString: string): string => {
    return dayjs(dateTimeString).format('DD/MM/YYYY');
  };

  const formatDateTime = (dateTimeString: string): string => {
    return dayjs(dateTimeString).format('DD/MM/YYYY h:mm A');
  };

  const formatDuration = (hours: number): string => {
    if (hours < 1) return `${Math.round(hours * 60)} minutes`;
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (minutes === 0) return `${wholeHours} hour${wholeHours !== 1 ? 's' : ''}`;
    return `${wholeHours}h ${minutes}m`;
  };

  const hasAdjustments = sessions.some(s => s.wasAdjusted);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorageIcon color="primary" />
          Database Preview: {templateName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Showing exactly what will be inserted into the database for {userName} ({userEmail})
        </Typography>
        
        {hasAdjustments && (
          <Alert severity="info" sx={{ mt: 2 }} icon={<WarningIcon />}>
            Some session dates have been adjusted to avoid weekends and UK bank holidays
          </Alert>
        )}
      </Box>

      {/* Quick Summary */}
      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'success.50' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            Database Records Summary
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Total Records</Typography>
              <Typography variant="h5" color="success.main">{sessions.length}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">First Session</Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(sessions[0]?.startDateTime)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Last Session</Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(sessions[sessions.length - 1]?.startDateTime)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">User Email</Typography>
              <Typography variant="body1" fontWeight="medium">
                {userEmail}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label={`${sessions.length} Database Records`} 
              size="small" 
              color="success" 
              variant="filled" 
            />
            <Chip 
              label="Complete Field Data" 
              size="small" 
              color="info" 
              variant="outlined" 
            />
            {hasAdjustments && (
              <Chip 
                label="Dates Adjusted" 
                size="small" 
                color="warning" 
                variant="filled" 
                icon={<WarningIcon />}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Detailed Database Records */}
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EventIcon color="primary" />
        Database Records to be Created
      </Typography>

      <Stack spacing={2}>
        {sessions.map((session, index) => (
          <Accordion key={index} variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {session.topic}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                  <Chip 
                    label={formatDate(session.startDateTime)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip 
                    label={formatTime(session.startDateTime)}
                    size="small"
                    variant="outlined"
                  />
                  {session.wasAdjusted && (
                    <Chip 
                      label={`Adjusted (${session.adjustmentReason})`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Field</strong></TableCell>
                      <TableCell><strong>Value</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>OnboardUserId</TableCell>
                      <TableCell>{session.onboardUserId}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Topic</TableCell>
                      <TableCell>{session.topic}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell>{session.description || 'NULL'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>StartDateTime</TableCell>
                      <TableCell>{formatDateTime(session.startDateTime)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>EndDateTime</TableCell>
                      <TableCell>{formatDateTime(session.endDateTime)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Duration</TableCell>
                      <TableCell>{formatDuration(session.duration)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Presenter</TableCell>
                      <TableCell>{session.presenter}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>PresenterEmail</TableCell>
                      <TableCell>{session.presenterEmail}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>EmployeeEmail</TableCell>
                      <TableCell>{session.employeeEmail}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>OtherAttendeeEmails</TableCell>
                      <TableCell>{session.otherAttendeeEmails || 'NULL'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>ResourceLinks</TableCell>
                      <TableCell>{session.resourceLinks || 'NULL'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>SessionCompleted</TableCell>
                      <TableCell>{session.sessionCompleted ? 'TRUE' : 'FALSE'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>ConfidenceLevel</TableCell>
                      <TableCell>{session.confidenceLevel || 'NULL'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>EmployeeFeedback</TableCell>
                      <TableCell>{session.employeeFeedback || 'NULL'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>LineManagerFeedback</TableCell>
                      <TableCell>{session.lineManagerFeedback || 'NULL'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>CreationDate</TableCell>
                      <TableCell>{formatDateTime(session.creationDate)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>UpdateDate</TableCell>
                      <TableCell>{formatDateTime(session.updateDate)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <Divider sx={{ my: 3 }} />
      
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Total Database Records: {sessions.length} • 
          All fields populated with real data • 
          Ready for insertion into toolbox_onboarding table
        </Typography>
      </Box>
    </Box>
  );
};

export default DatabasePreview;
